import React from 'react';
import { useBlocker, useParams } from 'react-router-dom';

import { useDebounce } from '@/utils/hooks';

import { hashOrder, useWindowStore } from '../api/windowStore';
import { AUTOSAVE_DEBOUNCE_DELAY } from '../constants';
import { useUpdateOrder } from '../queries/order/crud';
import { useUpdatePosition } from '../queries/position/crud';
import { hashPosition, PositionState } from './positionStore';
import { usePositionStore } from './positionStore';

export const useOrderAutosave = () => {
  const windowStore = useWindowStore();
  const { mutateAsync: updateOrder } = useUpdateOrder();

  const debouncedStore = useDebounce(
    windowStore,
    AUTOSAVE_DEBOUNCE_DELAY,
    null
  );

  React.useEffect(() => {
    if (!debouncedStore) {
      return;
    }

    if (hashOrder(debouncedStore) !== debouncedStore.hash) {
      updateOrder({ data: debouncedStore });
    }
  }, [debouncedStore, updateOrder]);
};

// this can't use useAutosave because it needs the blocker
// useAutosave can't be abstracted because we can have only one blocker per route
let storeRef: PositionState | null = null;
export const usePositionAutosave = () => {
  const { mutateAsync: updatePosition } = useUpdatePosition();
  const { positionId } = useParams();

  const positionStore = usePositionStore();

  const debouncedStore = useDebounce(
    positionStore,
    AUTOSAVE_DEBOUNCE_DELAY,
    positionStore
  );

  storeRef = positionStore;

  React.useEffect(() => {
    if (!debouncedStore) {
      return;
    }

    if (hashPosition(debouncedStore) !== debouncedStore.hash) {
      updatePosition({ data: debouncedStore });
    }
  }, [debouncedStore, updatePosition]);

  const flush = () => {
    if (!storeRef) {
      return;
    }

    if (positionId !== storeRef.id) {
      return;
    }

    const hasUnsavedChanges = hashPosition(storeRef) !== storeRef.hash;

    if (hasUnsavedChanges) {
      updatePosition({ data: storeRef });
    }
  };

  usePreRouteChange(flush);
};

// we can only have one blocker per route
export const usePreRouteChange = (onRouteLeave?: () => void) => {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const shouldRun = currentLocation.pathname !== nextLocation.pathname;

    if (shouldRun && onRouteLeave) {
      onRouteLeave();
    }

    return false; // Don't actually block, just detect
  });

  return blocker;
};
